'use client';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React from 'react';
import { useState, type ReactNode } from 'react';

import { Button } from '../../primitives/button';
import { Calendar } from '../../primitives/calendar';
import { Label } from '../../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { useLocalization } from '../../theme';
import { cn } from '../../utils/cn';

const startOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};
const endOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
};

export interface UzDatePickerProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  value?: Date | null;
  defaultValue?: Date | null;
  onValueChange?: (value: Date | null) => void;
  /** Disables specific dates. (date) => boolean. */
  disabled?: boolean | ((date: Date) => boolean);
  /**
   * Earliest selectable date. Days before this are greyed out AND the
   * calendar opens on `minDate`'s month if it's in the future - so a
   * check-in field bounded by the trip start doesn't force the operator
   * to page forward from today. Same idea for `maxDate` in the past.
   */
  minDate?: Date;
  /** Latest selectable date. Days after this are greyed out. */
  maxDate?: Date;
  /** Display format. Defaults to AppConfig's dateFormat. */
  displayFormat?: string;
  /** Right-side icon override. */
  icon?: ReactNode;
  containerClassName?: string;
  triggerClassName?: string;
}

export const UzDatePicker = ({
  label,
  hint,
  error,
  required,
  placeholder = 'Pick a date',
  value,
  defaultValue,
  onValueChange,
  disabled,
  minDate,
  maxDate,
  displayFormat,
  icon,
  containerClassName,
  triggerClassName,
}: UzDatePickerProps): React.JSX.Element => {
  const localization = useLocalization();
  const fmt = displayFormat ?? localization.dateFormat;

  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<Date | null>(defaultValue ?? null);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : internal;

  const handleSelect = (date: Date | undefined) => {
    const next = date ?? null;
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
    setOpen(false);
  };

  // Which month the calendar should land on when it opens. Prefer the
  // selected value; with no value, land on the first SELECTABLE month
  // rather than today - otherwise a field whose `minDate` is in the
  // future (e.g. sep only) opens on today's month (aug) with every
  // day greyed out, forcing the operator to page forward manually.
  const landingMonth = ((): Date => {
    if (selected) return selected;
    const today = new Date();
    if (minDate && today.getTime() < minDate.getTime()) return minDate;
    if (maxDate && today.getTime() > maxDate.getTime()) return maxDate;
    return today;
  })();

  // Combine minDate/maxDate with the caller's disabled predicate into a
  // single matcher the underlying DayPicker consumes.
  const disabledMatcher = ((): ((date: Date) => boolean) | undefined => {
    const callerFn = typeof disabled === 'function' ? disabled : undefined;
    if (!callerFn && !minDate && !maxDate) return undefined;
    return (d: Date) => {
      if (callerFn?.(d)) return true;
      if (minDate && d.getTime() < startOfDay(minDate).getTime()) return true;
      if (maxDate && d.getTime() > endOfDay(maxDate).getTime()) return true;
      return false;
    };
  })();

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <Label className={cn(typeof disabled === 'boolean' && disabled && 'opacity-60')}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={typeof disabled === 'boolean' ? disabled : false}
            className={cn(
              'w-full justify-start text-left font-normal',
              !selected && 'text-muted-foreground',
              error && 'border-destructive focus-visible:ring-destructive',
              triggerClassName,
            )}
            aria-invalid={!!error || undefined}
          >
            {icon ?? <CalendarIcon className="mr-2 h-4 w-4" />}
            {selected ? format(selected, fmt) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected ?? undefined}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            defaultMonth={landingMonth}
          />
        </PopoverContent>
      </Popover>
      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
